import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import type { User } from '@chatapp-inbox/shared';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { adminErrorMessage } from './error-message';
import { useUpdateUser } from './use-users';
import styles from './forms.module.css';
import panelStyles from './EditUserPanel.module.css';

interface UserAccessBlockProps {
  user: User;
  viewerId: string;
  /** O botão clicado deixou de existir (desativar vira reativar): o foco vai para o título do cartão. */
  focusHeading: () => void;
}

/**
 * Situação: desativar (com confirmação em dois passos) ou reativar. Desativar desconecta a pessoa e
 * devolve as conversas dela à fila das não atribuídas, então não é um clique solto. Reativar é
 * seguro e imediato (as conversas não voltam sozinhas).
 *
 * A própria conta não se desativa aqui: o servidor deixaria (se houver outro administrador), e a
 * pessoa seria desconectada no meio do uso.
 */
export function UserAccessBlock({
  user,
  viewerId,
  focusHeading,
}: UserAccessBlockProps): ReactElement {
  const headingId = useId();
  const promptId = useId();
  const isYou = user.id === viewerId;
  const update = useUpdateUser(viewerId);
  const status = useStatusMessage();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deactivateRef = useRef<HTMLButtonElement>(null);
  const keepRef = useRef<HTMLButtonElement>(null);
  // Para onde o foco vai quando a pergunta abre ou fecha. É um PEDIDO explícito (e não "a pergunta
  // fechou, volte ao botão"): ao dar certo a pergunta também fecha, mas o foco já foi para o título
  // do cartão, e devolvê-lo a um botão que o cache está prestes a trocar o deixaria no <body>.
  const returnFocus = useRef<'keep' | 'deactivate' | null>(null);

  useEffect(() => {
    const target = returnFocus.current;
    returnFocus.current = null;
    if (target === 'keep') keepRef.current?.focus();
    else if (target === 'deactivate') deactivateRef.current?.focus();
  }, [confirming]);

  const pending = update.isPending;

  const change = (active: boolean): void => {
    if (pending) return;
    setError(null);
    update.mutate(
      { id: user.id, patch: { active } },
      {
        onSuccess: (saved) => {
          returnFocus.current = null;
          setConfirming(false);
          status.announce(`${active ? 'Usuário reativado' : 'Usuário desativado'}: ${saved.name}.`);
          focusHeading();
        },
        onError: (failure) => {
          returnFocus.current = 'deactivate';
          setConfirming(false);
          setError(
            adminErrorMessage(
              failure,
              active
                ? 'Não foi possível reativar o usuário. Tente de novo.'
                : 'Não foi possível desativar o usuário. Tente de novo.',
            ),
          );
        },
      },
    );
  };

  return (
    <section aria-labelledby={headingId} className={panelStyles.block}>
      <h4 id={headingId} className={panelStyles.blockTitle}>
        Acesso
      </h4>

      {!user.active ? (
        <>
          <p className={styles.hint}>
            Este usuário está desativado e não consegue entrar. As conversas que eram dele não
            voltam sozinhas: continuam em “Não atribuídas”.
          </p>
          <div className={styles.actions}>
            <Button variant="primary" busy={pending} onClick={() => change(true)}>
              Reativar usuário
            </Button>
          </div>
        </>
      ) : isYou ? (
        <p className={styles.hint}>
          Você não pode desativar a sua própria conta. Peça a outro administrador.
        </p>
      ) : confirming ? (
        <div role="group" aria-labelledby={promptId} className={panelStyles.confirm}>
          <p id={promptId} className={panelStyles.confirmTitle}>
            Desativar este usuário?
          </p>
          <p className={styles.hint}>
            A pessoa será desconectada agora e não poderá mais entrar. As conversas dela voltam para
            “Não atribuídas”. Você pode reativar o usuário depois.
          </p>
          <div className={styles.actions}>
            <Button variant="danger" busy={pending} onClick={() => change(false)}>
              Sim, desativar
            </Button>
            <Button
              ref={keepRef}
              busy={pending}
              onClick={() => {
                returnFocus.current = 'deactivate';
                setConfirming(false);
              }}
            >
              Não, manter ativo
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          <Button
            ref={deactivateRef}
            variant="dangerOutline"
            onClick={() => {
              setError(null);
              returnFocus.current = 'keep';
              setConfirming(true);
            }}
          >
            Desativar usuário
          </Button>
        </div>
      )}

      {error && (
        <p role="alert" className={styles.alert}>
          {error}
        </p>
      )}
      <StatusRegion message={status.message} />
    </section>
  );
}
